import { useState } from "react";
import axios from "axios";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowDownRight,
  MapPin,
  Check,
  Loader2,
} from "lucide-react";
import { CLINIC } from "../lib/content";
import { CONDITIONS_MAP, CATEGORY_APPROACH } from "../lib/conditionContent";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingBook from "../components/FloatingBook";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { trackLeadSubmission } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["Morning", "Afternoon", "Evening", "Weekend"];

const fade = (delay = 0, y = 22) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function CarePage() {
  const { slug } = useParams();
  const condition = CONDITIONS_MAP[slug];

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferred_time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!condition) return <Navigate to="/" replace />;

  const approach = CATEGORY_APPROACH[condition.category] || "";
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please share your name, phone and email so we can confirm your request.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        condition: condition.name,
        name: form.name,
        phone: form.phone,
        email: form.email,
        preferred_time: form.preferred_time,
        message: form.message,
        source: `care/${slug}`,
      });
      trackLeadSubmission();
      setDone(true);
      toast.success("Thank you — your confirmation email is on its way.");
      setForm({ name: "", phone: "", email: "", preferred_time: "", message: "" });
    } catch {
      toast.error("Couldn't send. Please try again or call the clinic directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 sm:pt-28">
        {/* Split hero */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
          <motion.div {...fade(0)} className="mb-8">
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 text-xs font-sans font-extrabold uppercase tracking-tight text-muted-foreground hover:text-accent transition-colors"
            >
              <ArrowLeft size={13} /> Back home
            </Link>
          </motion.div>

          <div className="flex flex-col gap-8 sm:gap-10 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:items-start">
            {/* A — heading + quote (mobile: first) */}
            <div className="order-1 lg:col-start-1 lg:row-start-1 min-w-0">
              <motion.p
                {...fade(0.05)}
                className="overline text-accent"
                data-testid="cp-category"
              >
                {condition.category}
              </motion.p>
              <motion.h1
                {...fade(0.18)}
                data-testid="cp-title"
                className="mt-4 font-serif text-primary text-[2rem] sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight break-words"
              >
                {condition.name}
              </motion.h1>

              <motion.p
                {...fade(0.42)}
                className="mt-6 sm:mt-8 font-serif italic text-accent text-xl sm:text-3xl leading-snug max-w-2xl"
              >
                “{condition.intro}”
              </motion.p>
            </div>

            {/* C — rest of copy (mobile: after form) */}
            <div className="order-3 lg:col-start-1 lg:row-start-2 min-w-0">
              <motion.p
                {...fade(0.7)}
                className="text-foreground/85 text-base sm:text-lg leading-relaxed max-w-xl"
              >
                {condition.body}
              </motion.p>

              <motion.div {...fade(0.95)} className="mt-10 border-l-2 border-accent/30 pl-5 max-w-xl">
                <p className="text-[0.7rem] font-sans font-extrabold uppercase tracking-tight text-accent mb-2">
                  How we approach it
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {approach}
                </p>
              </motion.div>

              <motion.p {...fade(1.15)} className="mt-8 text-[0.7rem] text-muted-foreground max-w-md">
                For education only — this is not medical advice. Diagnosis and treatment plans are made in person.
              </motion.p>

              <motion.div {...fade(1.3)} className="mt-14">
                <p className="overline text-muted-foreground mb-3">Trained &amp; certified</p>
                <p
                  className="font-serif tracking-tight leading-[1] text-[2.4rem] xs:text-[3rem] sm:text-[5rem] lg:text-[6.5rem] break-words"
                  style={{
                    background:
                      "linear-gradient(90deg,#E4477A 0%,#C63367 45%,#8E2447 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                  data-testid="cp-qualifications"
                >
                  MRCOG · MRCPI
                </p>
                <p className="mt-3 font-serif text-primary tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  25 <span className="italic text-accent">years</span> in practice
                </p>
                <p className="mt-4 text-sm text-muted-foreground">
                  London (UK) &amp; Ireland · Royal College accreditation
                </p>
              </motion.div>
            </div>

            {/* B — compact lead form + map (mobile: second) */}
            <motion.div
              {...fade(0.55)}
              className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-28 min-w-0"
            >
              <form
                id="enquire"
                onSubmit={submit}
                data-testid="cp-form"
                className="bg-card border border-border rounded-[1.25rem] sm:rounded-[1.5rem] p-5 sm:p-7"
              >
                <p className="overline text-accent">Book with Dr. Yasmin</p>
                <p className="font-serif text-primary text-[1.9rem] sm:text-[2.4rem] font-bold mt-3 leading-[1.02] tracking-tight">
                  Book an appointment
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  About: <span className="font-semibold text-foreground">{condition.name}</span>
                </p>

                {done && (
                  <div
                    className="mt-4 rounded-xl bg-accent/12 border border-accent/30 p-3 text-sm text-foreground flex items-center gap-2"
                    data-testid="cp-success"
                  >
                    <Check size={16} className="text-accent" /> Thank you — a confirmation email is on its way.
                  </div>
                )}

                <div className="mt-5 space-y-3.5">
                  <div className="space-y-1.5">
                    <Label htmlFor="cp-name" className="text-xs">Name *</Label>
                    <Input
                      id="cp-name"
                      data-testid="cp-name"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cp-phone" className="text-xs">Phone *</Label>
                    <Input
                      id="cp-phone"
                      data-testid="cp-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 …"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cp-email" className="text-xs">Email *</Label>
                    <Input
                      id="cp-email"
                      type="email"
                      data-testid="cp-email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@email.com"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Best time to call</Label>
                    <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
                      <SelectTrigger data-testid="cp-time">
                        <SelectValue placeholder="When can we reach you?" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cp-msg" className="text-xs">Anything else? (optional)</Label>
                    <Textarea
                      id="cp-msg"
                      data-testid="cp-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="A short note if you'd like"
                      rows={2}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="cp-submit"
                  disabled={loading}
                  className="group mt-6 w-full inline-flex items-center gap-3 rounded-full bg-accent text-accent-foreground pl-1.5 pr-6 py-1.5 disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
                  style={{ boxShadow: "0 18px 40px -12px rgba(228,71,122,0.55)" }}
                >
                  <span className="w-12 h-12 rounded-full bg-white text-accent grid place-items-center group-hover:rotate-[-15deg] transition-transform duration-500 shrink-0">
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <ArrowDownRight size={18} />
                    )}
                  </span>
                  <span className="flex-1 text-center font-sans font-extrabold uppercase tracking-tight text-sm">
                    {loading ? "Sending…" : "Book an appointment"}
                  </span>
                </button>
                <p className="text-[0.65rem] text-muted-foreground text-center mt-3">
                  By submitting you agree to be contacted by the clinic and receive a confirmation email.
                </p>
              </form>

              {/* Map / visit block */}
              <div className="mt-4 rounded-[1.5rem] overflow-hidden border border-border bg-card">
                <a
                  href={CLINIC.mapLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cp-open-map"
                  className="block h-40 relative group"
                >
                  <iframe
                    title="Mom and More Clinic map"
                    src={CLINIC.mapEmbed}
                    width="100%"
                    height="100%"
                    style={{ border: 0, pointerEvents: "none" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </a>
                <div className="p-4 flex items-center gap-3">
                  <MapPin size={16} className="text-accent shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">Mom &amp; More · Mahadevapura</p>
                    <p className="text-[0.7rem] text-muted-foreground mt-0.5 truncate">{CLINIC.address}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Instagram follow section removed — the site footer already has it. */}
      </main>

      <Footer />
      <FloatingBook />
      <Toaster position="top-center" richColors />
    </div>
  );
}
